import { atom, atomFamily } from 'recoil'
import type { AtomEffect } from 'recoil'

import type { ItemControlState } from '@/types/itemControls'

/**
 * Default state for item controls.
 * Uses nested structure for logical grouping and selective updates.
 */
const defaultItemControlState: ItemControlState = {
  type: '',
  position: {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    z: 0,
    rotate: 0,
  },
  selection: {
    selected: false,
    isEditing: false,
    disabled: false,
    assetLocked: false,
  },
}

/**
 * Atom family storing control state for each item.
 * Items populate this with their state, overlay reads it to render controls.
 */
export const itemControlStateAtomFamily = atomFamily<ItemControlState, string>({
  key: 'itemControlState',
  default: defaultItemControlState,
})

/**
 * Atom to trigger the "convert to editable text" modal.
 * Set to item ID when overlay's convert icon is clicked.
 * PageText listens and shows modal when ID matches.
 */
export const showConvertTextModalForItemIdAtom = atom<string | null>({
  key: 'showConvertTextModalForItemId',
  default: null,
})

/**
 * The TinyMCE text item whose conversion into a Lotus text box waits on the
 * confirmation. Set to its id when the overlay's control is clicked;
 * `EditorTextBoxConvertModal` shows while it holds one and clears it on
 * cancel or confirm.
 */
export const showEditorTextBoxConvertModalForItemIdAtom = atom<string | null>({
  key: 'showEditorTextBoxConvertModalForItemId',
  default: null,
})

const EDITOR_TEXT_BOX_CONVERT_SKIP_CONFIRMATION_STORAGE_KEY =
  'EDITOR_TEXT_BOX_CONVERT_SKIP_CONFIRMATION'

const persistEditorTextBoxConvertSkipConfirmation: AtomEffect<boolean> = ({
  setSelf,
  onSet,
}) => {
  try {
    setSelf(
      window.localStorage.getItem(
        EDITOR_TEXT_BOX_CONVERT_SKIP_CONFIRMATION_STORAGE_KEY,
      ) === 'true',
    )
  } catch {
    // Storage unavailable — the confirmation keeps showing.
  }
  onSet((value) => {
    try {
      window.localStorage.setItem(
        EDITOR_TEXT_BOX_CONVERT_SKIP_CONFIRMATION_STORAGE_KEY,
        value ? 'true' : 'false',
      )
    } catch {
      // The choice applies for this visit; it just does not persist.
    }
  })
}

/**
 * Whether the conversion's confirmation stays away: set when someone
 * converts with its Stop seeing this? box checked, and kept in this browser,
 * so from then on the overlay's control converts at once.
 */
export const editorTextBoxConvertSkipConfirmationAtom = atom<boolean>({
  key: 'editorTextBoxConvertSkipConfirmation',
  default: false,
  effects_UNSTABLE: [persistEditorTextBoxConvertSkipConfirmation],
})

/**
 * Atom to toggle quick actions menu for an image item.
 * Set to item ID when overlay's quick actions icon is clicked.
 * PageImage listens and toggles its local quick actions state.
 * Reset to null after handling to allow repeated toggles.
 */
export const toggleQuickActionsForItemIdAtom = atom<string | null>({
  key: 'toggleQuickActionsForItemId',
  default: null,
})

/**
 * Atom to close quick actions menu for an image item.
 * Set to item ID when quick actions menu requests close.
 * PageImage listens and sets showQuickActions to false.
 * Reset to null after handling to allow repeated closes.
 */
export const closeQuickActionsForItemIdAtom = atom<string | null>({
  key: 'closeQuickActionsForItemId',
  default: null,
})

/**
 * Atom to trigger the service information template modal.
 * Set to item ID when overlay's service template icon is clicked.
 * PageText listens and shows modal when ID matches.
 */
export const showServiceTemplateModalForItemIdAtom = atom<string | null>({
  key: 'showServiceTemplateModalForItemId',
  default: null,
})

/**
 * Atom to trigger swapping the service information template.
 * Set to item ID when overlay's swap template icon is clicked.
 * PageText listens and calls handleSwapTemplate when ID matches.
 */
export const swapServiceTemplateForItemIdAtom = atom<string | null>({
  key: 'swapServiceTemplateForItemId',
  default: null,
})

/**
 * Atom tracking whether any item is currently being dragged.
 * Item.js sets this true in handleDrag, false in handleStop.
 * Note: Warnings remain visible during drag operations.
 */
export const itemDraggingAtom = atom<boolean>({
  key: 'itemDragging',
  default: false,
})

/**
 * Atom family for edge resize preview dimensions.
 * ClassicEdgeResizeControls (a classic image's edge handles) writes preview dimensions during drag.
 * Item.js reads and syncs to internalWidth/Height for live preview.
 * Cleared on mouse up after committing actual dimensions.
 */
export const edgeResizePreviewAtomFamily = atomFamily<
  { width: number; height: number } | null,
  string
>({
  key: 'edgeResizePreview',
  default: null,
})
